import { SparkRenderer, SparkXr } from "@sparkjsdev/spark";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { applyFlight } from "./Flight";
import { Panel } from "./Panel";
import { ScaleContinuum, type HandPose } from "./ScaleContinuum";
import { placeArc, UISurface } from "./SpatialUI";
import { requestComfortableFrameRate, XrInput } from "./XrInput";
import { loadPanorama, type LoadedPanorama } from "./PanoramaSource";
import {
  loadWorld,
  measureWorldFrame,
  type LoadedWorld,
  type WorldFrame
} from "./WorldSource";

/**
 * Worlds — Scale Continuum.
 *
 *   ?world=<folder>   load a generated Marble package from public/worlds/
 *   ?url=<splat>      load any SPZ/PLY directly
 *   ?quality=100k|500k|full
 *   ?fov=0..1         fixed foveation
 *   ?fbs=0.5..1.5     XR framebuffer scale
 *
 * Probe 1 measured this stack on Quest 3: the platform floor is clean (1K
 * splats renders vsync-capped), and cost is fragment-bound, so framebuffer
 * scale is a bigger lever than splat count. Those defaults are set accordingly
 * rather than left at whatever looked fine on a desktop.
 */

const params = new URLSearchParams(location.search);
const num = (key: string, fallback: number): number => {
  const raw = params.get(key);
  const parsed = raw === null ? NaN : Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const config = {
  folder: params.get("world"),
  panorama: params.get("pano"),
  url: params.get("url"),
  quality: (params.get("quality") ?? "500k") as "100k" | "500k" | "full",
  // Off by default: Spark's LoD pager needs a purpose-built LoD tree (see the
  // build-lod tool in Spark's repo). A plain SPZ export does not carry one, and
  // enabling the pager against a source without a tree renders nothing at all.
  lod: params.get("lod") === "1",
  /** Percentile of splat heights treated as the walkable floor. */
  floor: THREE.MathUtils.clamp(num("floor", 0), 0, 0.9),
  /** Manual height trim in metres, applied on top of the measured floor. */
  eye: num("eye", 0),
  foveation: THREE.MathUtils.clamp(num("fov", 1), 0, 1),
  framebufferScale: THREE.MathUtils.clamp(num("fbs", 0.8), 0.5, 1.5)
};

const statusEl = document.querySelector<HTMLElement>("#status")!;
const hintEl = document.querySelector<HTMLElement>("#hint")!;
const setStatus = (message: string): void => {
  statusEl.textContent = message;
};

// --- Renderer -------------------------------------------------------------
const renderer = new THREE.WebGLRenderer({
  antialias: false, // multisampling does nothing for splats and costs fill rate
  powerPreference: "high-performance"
});
renderer.setPixelRatio(1);
renderer.setSize(innerWidth, innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.xr.enabled = true;
document.body.append(renderer.domElement);

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x05080d);

const camera = new THREE.PerspectiveCamera(70, innerWidth / innerHeight, 0.03, 300);
camera.position.set(0, 1.6, 0.9);

// Input, poses, and the rig all come from Meta's XR input package rather than
// being hand-rolled. `xrOrigin` is the rig: locomotion moves it, and the head
// and per-hand grip/ray spaces live inside it, maintained from the XRFrame.
const xrInput = new XrInput(camera, scene);
const rig = xrInput.origin;
rig.add(camera);
scene.add(rig);

const orbit = new OrbitControls(camera, renderer.domElement);
orbit.target.set(0, 1.2, -0.4);
orbit.enableDamping = true;

// LoD is the whole point of Spark 2 — a virtual-memory splat pager that keeps
// only the relevant splats resident and thins density toward the periphery.
// Marble exports ship the tree it needs.
const spark = new SparkRenderer({
  renderer,
  maxStdDev: Math.sqrt(5), // Spark's recommended VR value
  enableLod: config.lod,
  lodSplatScale: 1,
  // Critical for stereo. Spark re-sorts and re-accumulates splats when it
  // updates; if that runs between the left and right eye draws, each eye is
  // handed a different splat ordering and the two images stop agreeing —
  // which reads as broken stereo alignment rather than as flicker. Spark's own
  // docs call for preUpdate:false under WebXR so the update lands after the
  // frame rather than inside it.
  preUpdate: false
});
scene.add(spark);

// Lights exist solely for the controller models.
//
// Nothing else in this scene needs them: Gaussian splats carry their own colour
// and are unlit, and the panels use basic materials. IWSDK's animated
// controllers are GLTF with PBR materials, though, so in a scene with no lights
// they render essentially black — present, correctly tracked, and invisible.
// A hemisphere fill plus one soft key is enough to read their form without
// competing with the world.
scene.add(new THREE.HemisphereLight(0xdfefff, 0x0d1a24, 2.2));
const controllerKey = new THREE.DirectionalLight(0xffffff, 1.6);
controllerKey.position.set(0.6, 1.8, 0.8);
scene.add(controllerKey);

// The world lives inside its own group so the Scale Continuum can move, turn
// and scale it without touching the splat mesh's own orientation fix.
const worldRoot = new THREE.Group();
scene.add(worldRoot);

const panel = new Panel();
scene.add(panel.group);

const continuum = new ScaleContinuum(worldRoot);

/**
 * Small spheres at the controller positions.
 *
 * Without them there is no way to tell, inside the headset, whether a grab
 * registered — and a grab that registers on only one hand silently degrades
 * into a translate, which looks like "scaling is broken" rather than "one
 * button was missed". They turn violet when that hand is grabbing, and a line
 * joins them while both are held so the distance driving the scale is visible.
 */
const handMarkers = [0, 1].map(() => {
  const marker = new THREE.Mesh(
    // Smaller now that real controller models are drawn: this exists only to
    // signal grab state, not to stand in for the controller itself.
    new THREE.SphereGeometry(0.012, 12, 10),
    new THREE.MeshBasicMaterial({ color: 0x6ee7ff, depthTest: false })
  );
  marker.renderOrder = 999;
  marker.visible = false;
  scene.add(marker);
  return marker;
});

const spanLine = new THREE.Line(
  new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(),
    new THREE.Vector3()
  ]),
  new THREE.LineBasicMaterial({ color: 0x8b7bff, depthTest: false })
);
spanLine.renderOrder = 999;
spanLine.visible = false;
scene.add(spanLine);

function updateHandMarkers(): void {
  const poses = [leftPose, rightPose];
  poses.forEach((pose, index) => {
    const marker = handMarkers[index]!;
    marker.visible = renderer.xr.isPresenting;
    marker.position.copy(pose.position);
    (marker.material as THREE.MeshBasicMaterial).color.setHex(
      pose.gripping ? 0x8b7bff : 0x6ee7ff
    );
    marker.scale.setScalar(pose.gripping ? 1.6 : 1);
  });

  const both = leftPose.gripping && rightPose.gripping;
  spanLine.visible = both && renderer.xr.isPresenting;
  if (both) {
    const positions = spanLine.geometry.getAttribute("position");
    positions.setXYZ(0, leftPose.position.x, leftPose.position.y, leftPose.position.z);
    positions.setXYZ(1, rightPose.position.x, rightPose.position.y, rightPose.position.z);
    positions.needsUpdate = true;
    spanLine.geometry.computeBoundingSphere();
  }
}

// A faint floor gives the eye something to judge scale against while the world
// is small. It fades out as the world grows past room scale.
const floor = new THREE.Mesh(
  new THREE.RingGeometry(0.35, 3.2, 64),
  new THREE.MeshBasicMaterial({
    color: 0x2d5f78,
    transparent: true,
    opacity: 0.25,
    side: THREE.DoubleSide
  })
);
floor.rotation.x = -Math.PI / 2;
floor.position.y = 0.01;
scene.add(floor);

// --- World ----------------------------------------------------------------
/** Radius, in metres, the world occupies when it is sitting in your hands. */
const HOLD_RADIUS_M = 0.26;

interface LibraryEntry {
  readonly kind: "world" | "panorama";
  readonly folder: string;
  readonly name: string;
}

interface Library {
  readonly worlds: readonly LibraryEntry[];
  readonly panoramas: readonly LibraryEntry[];
}

/** Everything switchable, worlds first, in a stable order. */
let library: LibraryEntry[] = [];
let libraryIndex = 0;

let world: LoadedWorld | null = null;
let panorama: LoadedPanorama | null = null;
let restScale = 1;
let lifeSizeScale = 1;
let groundSource = "none";
let heightTrim = 0;
let worldFrame: WorldFrame | null = null;
const restPosition = new THREE.Vector3(0, 1.15, -0.55);

async function loadLibrary(): Promise<void> {
  try {
    const response = await fetch("./library.json", { cache: "no-cache" });
    if (!response.ok) {
      return;
    }
    const parsed = (await response.json()) as Library;
    library = [...parsed.worlds, ...parsed.panoramas];
  } catch {
    // A missing index is not fatal — the app still honours ?world= / ?pano=.
  }
}

/** Tears the current subject out of the scene before another takes its place. */
function clearSubject(): void {
  for (const child of [...worldRoot.children]) {
    worldRoot.remove(child);
    const disposable = child as { dispose?: () => void };
    disposable.dispose?.();
  }
  world = null;
  panorama = null;
}

/**
 * Switches to a library entry.
 *
 * Every entry arrives with the same framing — held at HOLD_RADIUS_M, same rest
 * position — so switching compares like with like instead of dropping you into
 * a different vantage point each time.
 */
async function showEntry(index: number): Promise<void> {
  if (library.length === 0) {
    return;
  }
  libraryIndex = ((index % library.length) + library.length) % library.length;
  const entry = library[libraryIndex]!;
  clearSubject();
  setStatus(`Loading ${entry.name}…`);
  if (entry.kind === "panorama") {
    await bootPanorama(entry.folder);
  } else {
    await bootWorld(entry.folder);
  }
}

async function boot(): Promise<void> {
  await loadLibrary();

  if (config.panorama !== null || (config.url?.match(/\.(png|jpe?g|webp)$/i) ?? false)) {
    await bootPanorama(config.panorama);
    renderer.setAnimationLoop(tick);
    return;
  }

  // An explicit ?world= wins; otherwise start at the first library entry so the
  // installed app always opens on something rather than a blank scene.
  const requested = config.folder;
  const startIndex = requested === null
    ? 0
    : Math.max(0, library.findIndex((e) => e.folder === requested));
  if (library.length > 0 && requested === null) {
    await showEntry(startIndex);
  } else {
    await bootWorld(requested);
    libraryIndex = Math.max(0, startIndex);
  }
  renderer.setAnimationLoop(tick);
}

async function bootWorld(folder: string | null): Promise<void> {
  setStatus("Loading world…");
  world = await loadWorld({
    folder,
    url: config.url,
    quality: config.quality,
    onProgress: setStatus
  });
  worldRoot.add(world.mesh);

  // "Life size" has to mean life size, so prefer the metric scale Marble
  // reports over anything inferred from the geometry.
  //
  // The frame measurement replaces three earlier placement schemes that each
  // failed on this world — Marble's reported ground offset, a low-percentile
  // floor, and the capture origin — landing the viewer either buried in
  // geometry or floating above the path. Measuring the world's own centre and
  // floor from the splats is what finally made placement land consistently.
  worldFrame = measureWorldFrame(world.mesh);
  const extent = worldFrame?.radius ?? 0;

  if (world.metricScaleFactor !== null && world.metricScaleFactor > 0) {
    lifeSizeScale = world.metricScaleFactor;
    groundSource = "marble metric";
  } else {
    lifeSizeScale = extent > 0 ? 6 / extent : 1;
    groundSource = "measured";
  }


  // The rest pose is derived from the world's measured extent, not from a
  // fraction of life size. A garden and a single object have wildly different
  // life-size footprints, so a fixed divisor makes one hand-sized and leaves
  // the other room-sized. Sizing to a target radius makes every world arrive
  // as something you could actually cup in two hands.
  restScale = extent > 0 ? HOLD_RADIUS_M / extent : lifeSizeScale / 14;
  heightTrim = config.eye;
  if (worldFrame !== null) {
    world.mesh.position.set(
      -worldFrame.centreX,
      -worldFrame.floorY,
      -worldFrame.centreZ
    );
  }
  worldRoot.position.copy(restPosition);
  continuum.reset(restPosition, restScale);

  setStatus(
    `${world.name} — ${world.splatCount.toLocaleString()} splats in ${world.loadMs} ms`
  );
  autoEnterIfInstalled();
  hintEl.textContent = `Marble package · ${config.lod ? "LoD pager on" : "LoD off (plain SPZ export)"}`;
}

/**
 * A panorama joins the same continuum as a splat world: small, it is a hollow
 * globe you look into; large, it closes around you. The radius is fixed at 1 m
 * so the rest scale maths below is shared with world loading.
 */
async function bootPanorama(folder: string | null): Promise<void> {
  setStatus("Loading panorama…");
  panorama = await loadPanorama({
    folder,
    url: config.url,
    onProgress: setStatus
  });
  worldRoot.add(panorama.mesh);

  // A panorama sphere is authored at radius 1, so "life size" is the radius at
  // which it comfortably surrounds a standing person.
  lifeSizeScale = 3.2;
  // The panorama sphere is authored at radius 1, so its extent is 1.
  restScale = HOLD_RADIUS_M;
  worldRoot.position.copy(restPosition);
  continuum.reset(restPosition, restScale);

  setStatus(`${panorama.name} — loaded in ${panorama.loadMs} ms`);
  autoEnterIfInstalled();
  const seam = panorama.seamError;
  hintEl.textContent =
    seam === null
      ? `${panorama.truthLabel} · rotation only, no parallax`
      : `${panorama.truthLabel} · seam ${seam.toFixed(1)}/255 ${seam < 12 ? "(continuous)" : "(inspect)"} · rotation only`;
}

// --- XR -------------------------------------------------------------------
const sparkXr = new SparkXr({
  renderer,
  mode: "vr",
  referenceSpaceType: "local-floor",
  fixedFoveation: config.foveation,
  frameBufferScaleFactor: config.framebufferScale,
  enableHands: false
  // No `controllers` block: Spark's locomotion walks a horizontal plane and
  // claims the squeeze buttons. Flight.ts owns movement instead, so vertical
  // travel works and the grab buttons stay ours.
});

/**
 * Auto-enter the immersive session when running as an installed PWA.
 *
 * An installed immersive app opens with no 2D page and therefore no button to
 * press — tapping the app icon *is* the user activation, so the app must start
 * the session itself. Gated on `getDigitalGoodsService`, which exists only in
 * the installed PWA and never in a browser tab, so a tab still shows the normal
 * Enter VR button rather than grabbing the display unprompted.
 *
 * This is device-only and cannot be validated in a desktop browser.
 */
function autoEnterIfInstalled(): void {
  const nav = navigator as Navigator & {
    xr?: { isSessionSupported?: (mode: string) => Promise<boolean> };
  };
  if (!("getDigitalGoodsService" in window) || nav.xr?.isSessionSupported === undefined) {
    return;
  }
  nav.xr
    .isSessionSupported("immersive-vr")
    .then((supported) => {
      if (supported && !renderer.xr.isPresenting) {
        void sparkXr.toggleXr();
      }
    })
    .catch(() => {
      /* Fall back to the on-screen entry button. */
    });
}

/**
 * Remote debug controls.
 *
 * An ADB-injected keyevent arrives as a trusted DOM event, which both satisfies
 * WebXR's user-activation requirement for entering a session and lets the whole
 * interaction be exercised without a human wearing the headset. That matters
 * for the Scale Continuum specifically: it is the one part of this app whose
 * bugs only appear inside a live XR session, and stepping the scale from
 * outside is far faster than asking someone to put the headset on for each fix.
 *
 *   Enter        enter / exit VR
 *   Up / Down    grow / shrink the world
 *   Left/Right   turn the world
 *   R            reset to the rest pose
 */
const SCALE_STEP = 1.35;

function applyDebugScale(factor: number): void {
  const next = THREE.MathUtils.clamp(
    worldRoot.scale.x * factor,
    restScale * 0.25,
    lifeSizeScale * 4
  );
  // Scale about the point in front of the viewer rather than the world origin,
  // matching what the two-handed gesture does.
  const anchor = restPosition;
  worldRoot.position.sub(anchor).multiplyScalar(next / worldRoot.scale.x).add(anchor);
  worldRoot.scale.setScalar(next);
  continuum.adoptExternalScale(next);
}

/**
 * Places the viewer standing inside the world at true scale.
 *
 * Marble reports a metric scale factor and a ground-plane offset, and the two
 * together are what make this exact rather than eyeballed: the mesh is already
 * shifted so the captured ground sits at local y=0, so scaling by the metric
 * factor and parking the world at the origin of a `local-floor` reference space
 * puts the world's ground on the player's real floor. Standing height then
 * comes from the headset itself, which is why it feels like a place rather than
 * a model you have been shrunk into.
 *
 * The rig is reset at the same time so "inside" means the capture viewpoint,
 * not wherever locomotion happened to leave you.
 */
function enterWorld(): void {
  rig.position.set(0, heightTrim, 0);
  // Keep the small forward/upward offset that the rest pose uses; parking the
  // world exactly on the origin puts the viewer inside the densest part of the
  // capture, because a generated world's origin is its centre, not a doorway.
  worldRoot.position.copy(restPosition);
  rig.quaternion.identity();
  worldRoot.quaternion.identity();
  worldRoot.scale.setScalar(lifeSizeScale);
  continuum.adoptExternalScale(lifeSizeScale);
  panel.recenter();
}

/** Returns the world to arm's length in front of the viewer. */
function holdWorld(): void {
  rig.position.set(0, 0, 0);
  rig.quaternion.identity();
  // Re-centre the cloud on its own middle so the held object sits in front of
  // you rather than dangling off to one side by the capture's origin offset.
  if (worldFrame !== null && world !== null) {
    world.mesh.position.set(
      -worldFrame.centreX,
      -worldFrame.floorY,
      -worldFrame.centreZ
    );
  }
  continuum.reset(restPosition, restScale);
  panel.recenter();
}

addEventListener("keydown", (event) => {
  if (event.key === "Enter") {
    if (world !== null || panorama !== null) {
      void sparkXr.toggleXr();
    }
    return;
  }
  switch (event.key) {
    case "ArrowUp":
      applyDebugScale(SCALE_STEP);
      break;
    case "ArrowDown":
      applyDebugScale(1 / SCALE_STEP);
      break;
    case "ArrowLeft":
      worldRoot.rotateY(-Math.PI / 12);
      break;
    case "ArrowRight":
      worldRoot.rotateY(Math.PI / 12);
      break;
    case "m":
    case "M":
      setMenuOpen(!menuOpen);
      break;
    case "n":
    case "N":
      void showEntry(libraryIndex + 1);
      break;
    case "p":
    case "P":
      void showEntry(libraryIndex - 1);
      break;
    case "e":
    case "E":
      enterWorld();
      break;
    case "PageUp":
    case "]":
      heightTrim += 0.25;
      rig.position.y = heightTrim;
      break;
    case "PageDown":
    case "[":
      heightTrim -= 0.25;
      rig.position.y = heightTrim;
      break;
    case "h":
    case "H":
    case "r":
    case "R":
      holdWorld();
      break;
    default:
      break;
  }
});

// --- Controller poses -----------------------------------------------------
/** Adapters onto IWSDK's hand state, in the shape ScaleContinuum expects. */
const leftPose: HandPose = xrInput.left;
const rightPose: HandPose = xrInput.right;

const rayOrigin = new THREE.Vector3();
const rayDirection = new THREE.Vector3();

const pointer = new THREE.Line(
  new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, 0, -1)
  ]),
  new THREE.LineBasicMaterial({ color: 0x6ee7ff, transparent: true, opacity: 0.75 })
);
pointer.visible = false;
scene.add(pointer);

const libraryPanel = new UISurface();
const toolsPanel = new UISurface();
libraryPanel.visible = false;
toolsPanel.visible = false;
scene.add(libraryPanel.group, toolsPanel.group);
let menuOpen = false;
let hoveredRow: string | null = null;

/**
 * Reads controller poses from the XRFrame directly.
 *
 * The previous version walked `session.inputSources` with a counter and asked
 * three for `getController(i)`. That ordering is not guaranteed to match, so
 * the left hand's buttons could be paired with the right hand's position — the
 * separation between them then jumped around and scaling never engaged.
 * `frame.getPose(source.gripSpace, ...)` is authoritative and pairs pose to
 * source by construction.
 *
 * Poses come back in reference space, so they need the rig transform applied to
 * reach world space, since locomotion moves the rig.
 */
// --- Spatial menus --------------------------------------------------------
/**
 * Rebuilds both panels from current state.
 *
 * The library panel is the world switcher: every generated world and panorama,
 * with the active one marked. The tools panel carries the actions that were
 * previously keyboard-only, which is what turns this from something only a
 * developer can drive into something usable in the headset.
 */
function refreshMenus(): void {
  libraryPanel.setModel({
    eyebrow: "Library",
    title: "Worlds",
    rows: library.map((entry, index) => ({
      id: `entry:${index}`,
      label: entry.name,
      value: entry.kind === "panorama" ? "360" : "splat",
      selected: index === libraryIndex
    })),
    footer: "Point and pull the trigger to load"
  });

  const posture = world !== null || panorama !== null ? continuum.posture : "—";
  toolsPanel.setModel({
    eyebrow: "Tools",
    title: "Scale & view",
    rows: [
      { id: "enter", label: "Stand inside", value: "life size" },
      { id: "hold", label: "Hold it", value: "in hands" },
      { id: "up", label: "Raise view", value: `${heightTrim.toFixed(2)} m` },
      { id: "down", label: "Lower view", value: `${heightTrim.toFixed(2)} m` },
      { id: "recenter", label: "Bring menus here" },
      { id: "close", label: "Close menu" }
    ],
    footer: `Now: ${posture}`
  });
}

function setMenuOpen(open: boolean): void {
  menuOpen = open;
  libraryPanel.visible = open;
  toolsPanel.visible = open;
  if (open) {
    positionMenus();
    refreshMenus();
  } else {
    libraryPanel.clearHover();
    toolsPanel.clearHover();
    hoveredRow = null;
  }
}

function positionMenus(): void {
  const head = renderer.xr.isPresenting ? renderer.xr.getCamera() : camera;
  const m = head.matrixWorld.elements;
  const headPos = new THREE.Vector3().setFromMatrixPosition(head.matrixWorld);
  const yaw = Math.atan2(-m[8]!, -m[10]!);
  placeArc([libraryPanel, toolsPanel], headPos, yaw, 0.95);
}

function activateRow(id: string): void {
  if (id.startsWith("entry:")) {
    const index = Number(id.slice("entry:".length));
    void showEntry(index).then(refreshMenus);
    return;
  }
  switch (id) {
    case "enter":
      enterWorld();
      break;
    case "hold":
      holdWorld();
      break;
    case "up":
      heightTrim += 0.25;
      rig.position.y = heightTrim;
      break;
    case "down":
      heightTrim -= 0.25;
      rig.position.y = heightTrim;
      break;
    case "recenter":
      positionMenus();
      panel.recenter();
      break;
    case "close":
      setMenuOpen(false);
      break;
    default:
      break;
  }
  refreshMenus();
}

/**
 * Reads the pointing hand, updates hover, and handles selection.
 *
 * The trigger does double duty: it selects when a row is under the pointer and
 * otherwise falls through to vertical flight. Contextual rather than modal —
 * there is no "UI mode" to remember being in.
 */
function updateMenus(session: XRSession | null): boolean {
  if (!menuOpen || session === null) {
    pointer.visible = false;
    return false;
  }

  rayOrigin.copy(rightPose.position);
  rayDirection.set(0, 0, -1).applyQuaternion(xrInput.right.quaternion).normalize();

  hoveredRow =
    libraryPanel.pick(rayOrigin, rayDirection) ??
    toolsPanel.pick(rayOrigin, rayDirection);
  if (hoveredRow === null) {
    libraryPanel.clearHover();
    toolsPanel.clearHover();
  }

  pointer.visible = true;
  pointer.position.copy(rayOrigin);
  pointer.quaternion.copy(xrInput.right.quaternion);
  pointer.scale.setScalar(hoveredRow === null ? 1.4 : 0.9);
  (pointer.material as THREE.LineBasicMaterial).color.setHex(
    hoveredRow === null ? 0x3f6a80 : 0x6ee7ff
  );

  if (xrInput.selectPressed() && hoveredRow !== null) {
    activateRow(hoveredRow);
  }

  // Report whether the pointer is over a row, so flight can ignore the trigger
  // while it is being used to click.
  return hoveredRow !== null;
}

/** B / Y (or the menu button) toggles the panels, on the rising edge. */
function pollMenuButton(): void {
  if (xrInput.menuPressed()) {
    setMenuOpen(!menuOpen);
  }
}

// --- Loop -----------------------------------------------------------------
const clock = new THREE.Clock();
let frameMs = 0;

function tick(time: number, _frame?: XRFrame): void {
  const delta = Math.min(clock.getDelta(), 0.1);
  // Repainted outside the XR branch so the desktop preview shows the menus too;
  // verifying UI without putting the headset on is worth the two calls.
  let pointingAtUI = false;
  frameMs = frameMs * 0.9 + delta * 1000 * 0.1;

  if (renderer.xr.isPresenting) {
    // One call maintains the rig, the head, both grip/ray spaces, gamepad edge
    // state and the controller models.
    xrInput.update(renderer.xr, delta, time);
    const live = renderer.xr.getSession();
    if (live !== null) {
      requestComfortableFrameRate(live);
    }
    continuum.update(leftPose, rightPose);
    updateHandMarkers();

    const session = renderer.xr.getSession();
    pollMenuButton();
    pointingAtUI = updateMenus(session);

    // Flight is suppressed while grabbing so a stick nudge cannot fight the
    // world you are actively holding, and while the trigger is being used to
    // click a menu row so selecting does not also fly you upward.
    if (!continuum.isGrabbing) {
      const left = xrInput.left;
      const right = xrInput.right;
      applyFlight(
        rig,
        xrInput.head,
        {
          strafe: left.stick.x,
          forward: left.stick.y,
          turn: right.stick.x,
          // Right stick up, or either trigger, changes altitude. Suppressed
          // while pointing at a row so selecting does not also fly you upward.
          lift: pointingAtUI
            ? 0
            : THREE.MathUtils.clamp(
                -right.stick.y + right.trigger - left.trigger,
                -1,
                1
              ),
          boost: false,
          slow: false
        },
        delta
      );
    }
  } else {
    orbit.update();
  }

  // The reference ring is only useful while the world is small enough to be an
  // object; once you are inside the world it is visual noise.
  floor.material.opacity = THREE.MathUtils.clamp(
    0.28 * (1 - continuum.scale / (restScale * 6)),
    0,
    0.28
  );
  floor.visible = floor.material.opacity > 0.01;

  libraryPanel.update();
  toolsPanel.update();

  const activeCamera = renderer.xr.isPresenting
    ? renderer.xr.getCamera()
    : camera;

  if (panorama !== null) {
    const seam = panorama.seamError;
    panel.set({
      eyebrow: panorama.truthLabel,
      title: continuum.posture,
      rows: [
        ["scene", panorama.name],
        ["size", `${continuum.relativeScale.toFixed(2)}x`],
        ["seam", seam === null ? "unmeasured" : `${seam.toFixed(1)} / 255`],
        ["depth", "none — rotation only"],
        ["frame", `${frameMs.toFixed(1)} ms`]
      ],
      footer: continuum.isGrabbing
        ? "APART = grow   TOGETHER = shrink"
        : "Hold A + X on both controllers",
      accent: continuum.isGrabbing ? "#8b7bff" : "#6ee7ff"
    });
    panel.update(activeCamera, delta);
  } else if (world !== null) {
    panel.set({
      eyebrow: "Scale continuum",
      title: continuum.posture,
      rows: [
        ["world", `${world.name}  (${libraryIndex + 1}/${Math.max(library.length, 1)})`],
        ["splats", world.splatCount.toLocaleString()],
        ["size", `${Math.round((continuum.scale / lifeSizeScale) * 100)}% of life size`],
        ["frame", `${frameMs.toFixed(1)} ms`],
        ["lod", config.lod ? "pager on" : "off"],
        ["ground", `${groundSource}  trim ${heightTrim.toFixed(2)}m`],
        [
          "hands",
          `${leftPose.gripping ? "L HELD" : "l --"}  ${rightPose.gripping ? "R HELD" : "r --"}  ${leftPose.position.distanceTo(rightPose.position).toFixed(2)}m`
        ]
      ],
      footer: continuum.isGrabbing
        ? "APART = grow   TOGETHER = shrink   (distance sets size)"
        : "Hold A + X on both controllers",
      accent: continuum.isGrabbing ? "#8b7bff" : "#6ee7ff"
    });
    panel.update(activeCamera, delta);
  }

  renderer.render(scene, camera);
}

addEventListener("resize", () => {
  camera.aspect = innerWidth / innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(innerWidth, innerHeight);
});

Object.assign(window, {
  __studio: { scene, camera, worldRoot, continuum, spark, panel }
});

boot().catch((error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  setStatus(`Failed: ${message}`);
});
